import { isDynamicBase } from '@sb/integrations/utils';
import { WaypointStep } from '@sb/remote-control/step';
import type { Step as RcStep } from '@sb/remote-control/types';
import type { StepKind, Space } from '@sb/routine-runner';

import { permitsWaypointChild } from '../../step-rules/waypointParents';
import {
  hasExplicitTcpOption,
  pinsChildTooltip,
  WELD_CHILD_TOOLTIP_ACTION_REQUIRED,
} from '../../step-rules/weld';
import {
  type ValidationRule,
  type RoutineView,
  type ValidationContext,
} from '../../types';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import {
  parentPinsTooltip,
  reportSavedTooltipProblem,
} from '../../util/tooltipReference';
import {
  validateCodeExpression,
  validateLegacyTooltipSupplied,
  validateMotionSpeed,
  validateStoredConditionShape,
} from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';
import { getSiblingPosition } from './blockConnectionHelpers';

/**
 * Inline port of `checkPalletBaseDimensions` from
 * `libs/remote-control/step/waypoint.ts:28-90`. Returns true when the pallet
 * base is too small to accommodate the boxes laid out in `palletBoxes`.
 */
function checkPalletBaseDimensionsTooSmall(
  palletBoxes: Space.PalletBoxes,
  palletBase: Space.PalletBase,
): boolean {
  if (palletBoxes.layerPatterns.length === 0) return false;

  const boxTypesMap = new Map(
    palletBoxes.boxTypes.map((boxType) => [boxType.id, boxType]),
  );

  let maxX = 0;
  let maxY = 0;

  for (const layerPattern of palletBoxes.layerPatterns) {
    const boxType = boxTypesMap.get(layerPattern.boxTypeID);
    if (!boxType) continue;

    for (const box of layerPattern.boxList) {
      const boxWidth =
        box.rotation === 0 || box.rotation === 180
          ? boxType.width
          : boxType.depth;

      const boxDepth =
        box.rotation === 0 || box.rotation === 180
          ? boxType.depth
          : boxType.width;

      const boxMaxX = box.x + boxWidth;
      const boxMaxY = box.y + boxDepth;

      if (boxMaxX > maxX) maxX = boxMaxX;
      if (boxMaxY > maxY) maxY = boxMaxY;
    }
  }

  return maxX > palletBase.widthMM || maxY > palletBase.lengthMM;
}

class WaypointRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Waypoint', 'Waypoint');
  }

  protected validate({
    parents,
    args,
    routine,
    ctx,
    stepId,
    warn,
  }: CanonicalValidateArgs): void {
    type WaypointArgs = Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>;
    const a = args as WaypointArgs;
    const parentStepKind = this.getParentKind(parents);
    const spaceItems = this.getSpaceItems(routine, ctx);
    this.checkParentKind(parentStepKind);
    this.checkTcpOption(parentStepKind, a);
    this.checkTarget(a);
    this.checkCustomBaseMatchJointAngles(a, ctx);
    this.checkSafeHomePoseMatchJointAngles(a, spaceItems);
    this.checkPositionList(a, spaceItems);
    this.checkAnchoredVisionMatchJointAngles(a, spaceItems);
    warn(this.checkExpressionTargets(a));
    warn(this.checkOffsetExpressions(a));
    this.checkAddOffsetParent(parents, a);
    this.checkWeldSiblingPosition(parentStepKind, a, routine, stepId);
    this.checkPalletConfiguration(a, spaceItems);
    this.checkDynamicBase(a, ctx, parents, routine);
    this.checkDeterministicPlannerElastokin(a, ctx);
    warn(this.checkSavedTooltipAvailable(a, spaceItems, ctx, parents, routine));
    this.checkMotionSpeed(a);

    if (a.stopEarlyConditions) {
      validateStoredConditionShape(
        a.stopEarlyConditions,
        'stopEarlyConditions',
      );
    }

    // Collected rather than thrown, so it cannot stand in for an error a later
    // check would have raised — including the runner schema cross-check, which
    // runs after this method returns and so no ordering here can protect.
    warn(
      validateLegacyTooltipSupplied({
        stepNoun: 'waypoint',
        tcpOption: a.tcpOption,
        gripperID: a.gripperID,
        ctx,
      }),
    );
  }

  /**
   * A waypoint left on `'auto'` has to have a saved tooltip to read. The
   * rule is shared with the arc, so the two step kinds cannot come to different
   * answers about the same position.
   *
   * Only `'auto'` is judged: `'active'` and `'parent'` name a frame something
   * else supplies, and a tooltip named outright answers for itself.
   *
   * Behind `setActiveTooltip`, because `'auto'` is the default every waypoint
   * carries rather than a selection anybody made. The flag is what says this
   * robot's operator has the picker to act on the message.
   *
   * A pallet or a vision list throws, because no equipment change can ever
   * give it a tooltip to read. The rest are returned to `warn()` and stop
   * nothing: fitting or enabling the right gripper makes them valid again.
   *
   * @throws {ActionRequiredError}
   */
  private checkSavedTooltipAvailable(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
    spaceItems: Space.Item[],
    ctx: ValidationContext,
    parents: CanonicalValidateArgs['parents'],
    routine: CanonicalValidateArgs['routine'],
  ): WarningFinding | undefined {
    if (!ctx?.featureFlags?.setActiveTooltip) return undefined;
    if (args.tcpOption !== 'auto') return undefined;
    if (parentPinsTooltip({ parents, routine })) return undefined;

    return reportSavedTooltipProblem({
      targets: [
        {
          targetKind: args.targetKind,
          target: args.target,
          positionListID: args.positionListID,
          positionListIndex: args.positionListIndex,
          palletMoveType: args.palletConfig?.moveType,
          // The dynamic-base pass re-runs a joint-matched target with pose
          // generation forced, and that pass does resolve a tooltip — so a
          // waypoint that matches the base is not joint-matched for this rule.
          matchesJointAngles:
            WaypointStep.emittedJointMatching(args) &&
            !WaypointStep.emittedDynamicBaseMatching(args),
        },
      ],
      spaceItems,
      equipment: ctx?.equipment,
      stepNoun: 'waypoint',
    });
  }

  /**
   * "Maintain robot configuration" (the deterministic planner) pins motion to a
   * single Elastokin IK branch, so it cannot run when the Elastokin IK solver
   * is disabled via the `disableElastokinIK` feature flag. Block execution so
   * the user resolves the conflict explicitly.
   *
   * @throws {ActionRequiredError}
   */
  private checkDeterministicPlannerElastokin(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
    ctx: ValidationContext,
  ): void {
    if (args.useDeterministicPlanner && ctx.featureFlags?.disableElastokinIK) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          '“Maintain robot configuration” requires the Elastokin IK solver, which is disabled (the disableElastokinIK feature flag is on). Turn off “Maintain robot configuration” or re-enable Elastokin IK to run this routine.',
        fieldId: 'useDeterministicPlanner',
      });
    }
  }

  /**
   * Surface legacy `customLimits` percent-based fields as a deprecation
   * prompt.
   *
   * @throws {Error}
   */
  private checkMotionSpeed(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
  ): void {
    validateMotionSpeed('Waypoint', args.motionSpeed);
  }

  /**
   * Get the immediate parent kind (if any).
   *
   * @returns {string} Parent stepKind or empty string
   */
  private getParentKind(
    parents: Array<{ stepKind: StepKind | string }>,
  ): string {
    return parents?.[0]?.stepKind ?? '';
  }

  /**
   * Ensure parent kind is allowed for Waypoint.
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkParentKind(parentStepKind: string): void {
    if (!permitsWaypointChild(parentStepKind)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Waypoint steps may only be used inside a Move Arm, Weld, or Rotary weld step',
      });
    }
  }

  /**
   * Reject a Waypoint that names its own tooltip inside a Weld step; see
   * `WELD_CHILD_TOOLTIP_ACTION_REQUIRED`.
   *
   * @throws {ActionRequiredError}
   */
  private checkTcpOption(
    parentStepKind: string,
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
  ): void {
    if (!pinsChildTooltip(parentStepKind)) return;
    if (!hasExplicitTcpOption(args)) return;

    throw new ActionRequiredError(WELD_CHILD_TOOLTIP_ACTION_REQUIRED);
  }

  /**
   * Ensure target pose or jointAngles are configured when required.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkTarget(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
  ): void {
    const target = args.target as Space.Position | null;

    const hasTarget =
      !!target && (target.jointAngles != null || target.pose != null);

    if (args.targetKind === 'singlePosition' && !hasTarget) {
      throw new ActionRequiredError({
        kind: 'needsTeaching',
        message: 'Joint angles or pose must be set',
        fieldId: 'jointAngles',
      });
    }
  }

  /**
   * Block shouldMatchJointAngles when a custom base is enabled. Relative
   * waypoints are exempt — they default shouldMatchJointAngles to false at
   * conversion time.
   *
   * @throws {ActionRequiredError}
   */
  private checkCustomBaseMatchJointAngles(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
    ctx: ValidationContext,
  ): void {
    if (ctx.featureFlags?.allowCustomBaseMatchJointAngles) return;
    if (!args.shouldMatchJointAngles) return;
    if (args.targetKind === 'relative') return;

    const hasCustomBase = (ctx.equipment ?? []).some(
      (item) => item.config.kind === 'CustomBase' && item.isEnabled,
    );

    if (!hasCustomBase) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: 'Match Joint angles may not be used with a custom base',
      fieldId: 'shouldMatchJointAngles',
    });
  }

  /**
   * Require `shouldMatchJointAngles` when the waypoint targets a `safeHomePose`
   * space item. A safe home pose is a specific joint configuration; without
   * matching joint angles the move resolves to the Cartesian pose alone, which
   * IK may reach in a different (and not necessarily safe) configuration.
   *
   * @throws {ActionRequiredError}
   */
  private checkSafeHomePoseMatchJointAngles(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
    spaceItems: Space.Item[],
  ): void {
    if (args.targetKind !== 'positionList' || !args.positionListID) return;
    if (args.shouldMatchJointAngles) return;

    const spaceItem = spaceItems.find((s) => s.id === args.positionListID);

    if (spaceItem?.kind === 'safeHomePose') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Match joint angles is required for safeHomePose targets',
        fieldId: 'shouldMatchJointAngles',
      });
    }
  }

  /**
   * Validate positionList configuration: must have a positionListID, the
   * referenced space item must exist and not be flagged unreachable, gridded
   * lists must have positions, and explicit indices must be in bounds.
   * (visionPositionList is exempt from the bounds check — populated at
   * runtime.)
   *
   * @throws {ActionRequiredError}
   */
  private checkPositionList(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
    spaceItems: Space.Item[],
  ): void {
    if (args.targetKind !== 'positionList') return;

    if (!args.positionListID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Position list must be set',
        fieldId: 'positionListID',
      });
    }

    const spaceItem = spaceItems.find((s) => s.id === args.positionListID);

    if (spaceItem?.warnings?.some((w) => w.kind === 'unreachable') === true) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'The referenced space item contains unreachable positions. Please update the positions before running.',
        fieldId: 'positionListID',
      });
    }

    if (spaceItem == null) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Choose a valid space item',
        fieldId: 'positionListID',
      });
    }

    if (
      spaceItem.kind === 'gridPositionList' &&
      (!spaceItem.positions || spaceItem.positions.length === 0)
    ) {
      throw new ActionRequiredError({
        kind: 'needsTeaching',
        message: `Generate a position list for ${spaceItem.name} before using it in a routine.`,
        fieldId: 'positionListID',
      });
    }

    // An untaught point list has no poses yet, so treat it as unset and report
    // it like a missing space item — clearer than an out-of-bounds index error,
    // and matching the Arc step. Only operator-taught point lists are checked
    // here; lists populated elsewhere (at runtime, or with their layout stored
    // separately) are left to their own validation.
    if (
      (spaceItem.kind === 'singlePosition' ||
        spaceItem.kind === 'freeformPositionList') &&
      spaceItem.positions.length === 0
    ) {
      throw new ActionRequiredError({
        kind: 'needsTeaching',
        message: 'The space item must be set',
        fieldId: 'positionListID',
      });
    }

    if (
      args.positionListIndex != null &&
      spaceItem.kind !== 'visionPositionList' &&
      'positions' in spaceItem &&
      Array.isArray(spaceItem.positions) &&
      (args.positionListIndex < 0 ||
        args.positionListIndex >= spaceItem.positions.length)
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Choose a valid index for the position list.',
        fieldId: 'positionListIndex',
      });
    }
  }

  /**
   * Block `shouldMatchJointAngles` when the referenced position list is an
   * anchored item (`anchoredToID != null`) or a vision position list — the
   * joint solution is not stable for these. Surfaced as a per-step error so the
   * operator can turn the toggle off in place (rather than the routine being
   * un-editable, which is what the old server-side hard throw caused).
   *
   * @throws {ActionRequiredError}
   */
  private checkAnchoredVisionMatchJointAngles(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
    spaceItems: Space.Item[],
  ): void {
    if (args.targetKind !== 'positionList' || !args.positionListID) return;
    if (!args.shouldMatchJointAngles) return;

    const spaceItem = spaceItems.find((s) => s.id === args.positionListID);
    if (!spaceItem) return;

    const isAnchored = spaceItem.anchoredToID != null;
    const isVision = spaceItem.kind === 'visionPositionList';

    if (isAnchored || isVision) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Match joint angles is not allowed for anchored or vision positions. ' +
          'Turn off "Match joint angles" to run this routine.',
        fieldId: 'shouldMatchJointAngles',
      });
    }
  }

  /**
   * Validate the expression target itself (when targetKind=expression) and the
   * relative+expression special case (positionListID === 'expression' requires
   * an expression). Deprecated syntax is recorded via `warn`.
   *
   * @throws {ActionRequiredError}
   */
  private checkExpressionTargets(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
  ): WarningFinding | undefined {
    const deprecation =
      args.targetKind === 'expression' && args.expression != null
        ? validateCodeExpression(
            args.expression,
            'expression',
            'Position expression',
          )
        : undefined;

    if (
      args.targetKind === 'relative' &&
      args.positionListID === 'expression' &&
      !args.expression
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Expression must be set',
        fieldId: 'expression',
      });
    }

    return deprecation;
  }

  /**
   * Validate the six relative-config offset expressions. Invalid syntax
   * throws; deprecated syntax is returned.
   *
   * @throws {ActionRequiredError}
   */
  private checkOffsetExpressions(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
  ): Array<WarningFinding | undefined> {
    const r = args.relativeConfig;
    if (!r) return [];

    return [
      validateCodeExpression(r.offsetX, 'offsetX', 'X offset'),
      validateCodeExpression(r.offsetY, 'offsetY', 'Y offset'),
      validateCodeExpression(r.offsetZ, 'offsetZ', 'Z offset'),
      validateCodeExpression(r.offsetRx, 'offsetRx', 'Roll offset'),
      validateCodeExpression(r.offsetRy, 'offsetRy', 'Pitch offset'),
      validateCodeExpression(r.offsetRz, 'offsetRz', 'Yaw offset'),
    ];
  }

  /**
   * Forbid `shouldMatchJointAngles` when ANY ancestor is an `AddOffset` step —
   * only the tooltip pose can be guaranteed under an offset.
   *
   * @throws {ActionRequiredError}
   */
  private checkAddOffsetParent(
    parents: Array<{ stepKind: StepKind | string }>,
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
  ): void {
    const insideAddOffset = parents.some((p) => p?.stepKind === 'AddOffset');

    if (insideAddOffset && (args.shouldMatchJointAngles ?? false)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Waypoint steps inside an Add Offset step may not require exact final joint position, as only the tooltip pose can be guaranteed.',
        fieldId: 'shouldMatchJointAngles',
      });
    }
  }

  /**
   * For Weld parents, `shouldMatchJointAngles` is only allowed on the FIRST
   * waypoint sibling.
   *
   * @throws {ActionRequiredError}
   */
  private checkWeldSiblingPosition(
    parentStepKind: string,
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
    routine: RoutineView,
    stepId: string,
  ): void {
    if (parentStepKind !== 'Weld') return;
    if (!args.shouldMatchJointAngles) return;

    const pos = getSiblingPosition(routine, stepId);

    if (pos > 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Match Joint Angles can only be enabled for the first waypoint in a Weld step.',
        fieldId: 'shouldMatchJointAngles',
      });
    }
  }

  /**
   * Pallet-specific checks when the referenced space item is a `palletBoxes`.
   *
   * For `approach` / `box` moves: a `selectedPalletBaseID` must be set, the
   * referenced pallet base must exist, and the base dimensions must
   * accommodate the pallet boxes.
   *
   * For `pickup` moves: a `selectedBoxType` must be set.
   *
   * Also runs on `relative` targetKind: re-check empty `gridPositionList`
   * (since `checkPositionList` only runs on `targetKind === 'positionList'`).
   *
   * @throws {ActionRequiredError}
   */
  private checkPalletConfiguration(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
    spaceItems: Space.Item[],
  ): void {
    if (args.targetKind !== 'positionList' && args.targetKind !== 'relative') {
      return;
    }

    if (!args.positionListID) return;

    const spaceItem = spaceItems.find((s) => s.id === args.positionListID);
    if (!spaceItem) return;

    if (
      spaceItem.kind === 'gridPositionList' &&
      (!spaceItem.positions || spaceItem.positions.length === 0)
    ) {
      throw new ActionRequiredError({
        kind: 'needsTeaching',
        message: `Generate a position list for ${spaceItem.name} before using it in a routine.`,
        fieldId: 'positionListID',
      });
    }

    if (spaceItem.kind !== 'palletBoxes') return;

    const moveType = args.palletConfig?.moveType;

    if (
      (moveType === 'approach' || moveType === 'box') &&
      !args.palletConfig?.selectedPalletBaseID
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Pallet base must be selected when using approach or box position',
        fieldId: 'palletConfig.selectedPalletBaseID',
      });
    }

    if (
      (moveType === 'approach' || moveType === 'box') &&
      args.palletConfig?.selectedPalletBaseID
    ) {
      const palletBase = spaceItems.find(
        (item) => item.id === args.palletConfig?.selectedPalletBaseID,
      );

      if (!palletBase) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Pallet base not found',
          fieldId: 'palletConfig.selectedPalletBaseID',
        });
      }

      if (
        checkPalletBaseDimensionsTooSmall(
          spaceItem,
          palletBase as Space.PalletBase,
        )
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message:
            'Pallet base dimensions are too small for selected pallet boxes',
          fieldId: 'palletConfig.selectedPalletBaseID',
        });
      }
    }

    if (moveType === 'pickup' && !args.palletConfig?.selectedBoxType) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Box type must be selected when using pickup position',
        fieldId: 'palletConfig.selectedBoxType',
      });
    }

    // The pallet stack's internal references resolve by plain string id at
    // run time, so a dangling id here otherwise reaches the physical robot
    // before anything reports it. Each check is scoped to references the
    // runtime actually dereferences — the editor leaves stale ids behind
    // (deleting a box type keeps the steps and patterns that named it), and
    // an id nothing will read must not block play.
    const selectedBoxType = args.palletConfig?.selectedBoxType;
    const boxTypes = spaceItem.boxTypes ?? [];

    // The runner reads selectedBoxType only for pickup moves; on
    // approach/box moves the field is inert.
    if (
      moveType === 'pickup' &&
      selectedBoxType != null &&
      !boxTypes.some((boxType) => boxType.id === selectedBoxType)
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `Box type '${selectedBoxType}' is not defined on ${spaceItem.name}`,
        fieldId: 'palletConfig.selectedBoxType',
      });
    }

    const boxTypeIds = new Set(boxTypes.map((boxType) => boxType.id));
    const layerPatterns = spaceItem.layerPatterns ?? [];
    const layerOrder = spaceItem.layerOrder ?? [];
    const orderedPatternIds = new Set(layerOrder);

    // Only patterns reachable through layerOrder are dereferenced at run
    // time; an orphaned pattern is dead data, not a runnable defect.
    for (const pattern of layerPatterns) {
      if (
        orderedPatternIds.has(pattern.id) &&
        !boxTypeIds.has(pattern.boxTypeID)
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `Layer pattern '${pattern.name || pattern.id}' references box type '${pattern.boxTypeID}', which is not defined on ${spaceItem.name}`,
          fieldId: 'positionListID',
        });
      }
    }

    const layerPatternIds = new Set(layerPatterns.map((pattern) => pattern.id));

    // A dangling layerOrder entry never errors at run time — the layer walk
    // skips it silently and that layer's boxes are simply never placed —
    // which is exactly why it must be reported here.
    for (const entry of layerOrder) {
      if (!layerPatternIds.has(entry)) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `Layer order entry '${entry}' does not match any layer pattern on ${spaceItem.name}`,
          fieldId: 'positionListID',
        });
      }
    }
  }

  /**
   * `shouldMatchDynamicBasePosition` requires (a) at least one enabled
   * dynamic-base equipment, (b) a non-`relative` target, and (c) a parent
   * MoveArmToV2 that moves the dynamic base to reach positions. The runner
   * builds its lift compensator from the parent's flag alone; without it the
   * waypoint flag is never acted on and the lift stays put with no error.
   *
   * @throws {ActionRequiredError}
   */
  private checkDynamicBase(
    args: Extract<RcStep.Arguments, { argumentKind: 'Waypoint' }>,
    ctx: ValidationContext,
    parents: Array<{ id: string; stepKind: StepKind | string }>,
    routine: RoutineView,
  ): void {
    if (!args.shouldMatchDynamicBasePosition) return;

    const equipment = ctx.equipment ?? [];

    const hasDynamicBase = equipment.some(
      (item) => isDynamicBase(item.config.kind) && item.isEnabled,
    );

    if (!hasDynamicBase) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Match Dynamic Base Position may only be enabled when a dynamic base is available',
        fieldId: 'shouldMatchDynamicBasePosition',
      });
    }

    if (args.targetKind === 'relative') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Match Dynamic Base Position may not be enabled for relative waypoints',
        fieldId: 'shouldMatchDynamicBasePosition',
      });
    }

    const parent = parents[0];

    if (parent?.stepKind !== 'MoveArmToV2') return;

    const parentArgs = routine.stepConfigurations[parent.id]?.args;

    const parentMovesDynamicBase =
      parentArgs?.argumentKind === 'MoveArmToV2' &&
      (parentArgs.moveDynamicBaseToReachPosition ?? false);

    if (!parentMovesDynamicBase) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Match Dynamic Base Position requires "Move dynamic base to reach position" to be enabled on the parent Move step',
        fieldId: 'shouldMatchDynamicBasePosition',
      });
    }
  }
}

export const WaypointValidation: ValidationRule =
  new WaypointRule().toValidationRule();
